import { useProjectStore } from '@talon/web/src/context';
import { Text, TextProps } from 'grommet';
import { Channel, DocumentText, Group, Home, IconProps, Resources, UserManager, Video } from 'grommet-icons';
import { kebabCase, startCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnchorLink } from './AnchorLink';
import { MenuWithChildren } from './MenuWithChildren';

export type AgencyMenusProps = {
  textColor?: TextProps['color'];
  iconColor?: IconProps['color'];
};

export const AgencyMenus = observer(({ textColor = 'accent-1', iconColor = 'accent-1' }: AgencyMenusProps) => {
  /** Context **/
  const navigate = useNavigate();
  const projectStore = useProjectStore();

  /** State **/
  const projectPath = useMemo(
    () => `/project/${kebabCase(projectStore.currentProject?.name)}`,
    [projectStore.currentProject]
  );

  const iconSize = '22px';

  /** Render **/

  const menuItemLinks = {
    'pre-pro': [
      { label: 'Project Home', onClick: () => navigate(projectPath), id: '/project' },
      {
        label: 'Session Estimator',
        onClick: () => navigate(`${projectPath}/session-estimator`),
        id: '/session-estimator',
      },
      { label: 'Usage Estimator', onClick: () => navigate(`${projectPath}/usage-estimator`), id: '/usage-estimator' },
      { label: 'Estimates', onClick: () => navigate(`${projectPath}/estimate`), id: '/estimate' },
    ],
    production: [
      {
        label: 'Licensing & Rights',
        onClick: () => navigate(`${projectPath}/licensing-rights`),
        id: '/licensing-rights',
      },
      { label: 'Hire Talent', onClick: () => navigate(`${projectPath}/hire-talent`), id: '/hire-talent' },
      { label: 'Time Tracking', onClick: () => navigate(`${projectPath}/time-tracking`), id: '/time-tracking' },
      { label: 'Pay Sessions', onClick: () => navigate(`${projectPath}/pay-sessions`), id: '/pay-sessions' },
      { label: 'Talent Manager', onClick: () => navigate(`${projectPath}/talent-manager`), id: '/talent-manager' },
      { label: 'Holding Fees', onClick: () => navigate(`${projectPath}/holding-fees`), id: '/holding-fees' },
    ],
    'post-production': [
      { label: 'Edits & Final Cast', onClick: () => navigate(`${projectPath}/edits`), id: '/edits' },
      // { label: 'Pay Residuals', onClick: () => navigate(`${projectPath}/pay-residuals`), id: '/pay-residuals' },
      // {
      //   label: 'Commercial History',
      //   onClick: () => navigate(`${projectPath}/commercial-history`),
      //   id: '/commercial-history',
      // },
      { label: 'Asset Library', onClick: () => navigate(`${projectPath}/asset-library`), id: '/asset-library' },
    ],
    // reports: [
    //   { label: 'Project Report', onClick: () => navigate(`${projectPath}/project-report`), id: '/project-report' },
    //   { label: 'Talent Report', onClick: () => navigate(`${projectPath}/talent-report`), id: '/talent-report' },
    //   {
    //     label: 'Residuals Report',
    //     onClick: () => navigate(`${projectPath}/residuals-report`),
    //     id: '/residuals-report',
    //   },
    // ],
  };

  const menuItems = Object.entries(menuItemLinks).map(([key, items]) => ({
    label: startCase(key.replaceAll(/-/g, ' ')),
    icon:
      key === 'pre-pro' ? (
        <Home size={iconSize} color={iconColor} />
      ) : key === 'production' ? (
        <Video size={iconSize} color={iconColor} />
      ) : key === 'post-production' ? (
        <Channel size={iconSize} color={iconColor} />
      ) : (
        <DocumentText size={iconSize} color={iconColor} />
      ),
    items: items.map((item) => ({
      ...item,
      color: location.pathname === item.id ? 'accent-3' : 'text',
    })),
  }));

  return (
    <>
      {projectStore.currentProject?.name &&
        menuItems.map((menu) => (
          <MenuWithChildren key={menu.label} label={menu.label} icon={menu.icon} items={menu.items} />
        ))}

      <AnchorLink
        to={'/performers'}
        label={
          <Text size="1rem" weight={500} color={textColor}>
            Performers
          </Text>
        }
        icon={<Group size={iconSize} color={iconColor} />}
        alignSelf="center"
      />

      <AnchorLink
        to={'/marketplace'}
        label={
          <Text size="1rem" weight={500} color={textColor}>
            Marketplace
          </Text>
        }
        icon={<UserManager size={iconSize} color={iconColor} />}
        alignSelf="center"
      />

      <AnchorLink
        to={'/resources'}
        label={
          <Text size="1rem" weight={500} color={textColor}>
            Resources
          </Text>
        }
        icon={<Resources size={iconSize} color={iconColor} />}
        alignSelf="center"
      />
    </>
  );
});
